import React from "react";
import { Warning } from "@chemaxon/design-system/color";

import MUIDataTable, { MUIDataTableOptions } from "mui-datatables";
import SynergyMUIDataTableSearchBox from "@chemaxon/terminus-client/ui/components/mui-datatables-search-box/SynergyMUIDataTableSearchBox";
import Tooltip from "@mui/material/Tooltip";

import "@chemaxon/terminus-client/ui/components/team-members/TeamMembersList.scss";
import {
  StatusOrder,
  TeamMember,
  UserStatus,
} from "@chemaxon/terminus-client/ui/models/TeamMember";
import WarningIcon from "@chemaxon/design-system/icons/Warning";
import { Typography } from "@chemaxon/design-system/typography";
import moment from "moment";

type TableType = "UsersTable" | "OperatorsTable";

function MemberList(props: Props) {
  const getMuiTableOptions = (tableType: TableType): MUIDataTableOptions => {
    return {
      customSearchRender: (searchText, handleSearch, hideSearch, options) => {
        return (
          <SynergyMUIDataTableSearchBox
            searchText={searchText}
            onSearch={handleSearch}
            onHide={hideSearch}
            options={options}
          />
        );
      },
      filterType: "dropdown",
      searchOpen: true,
      download: false,
      print: false,
      selectableRows: "none",
      elevation: 0,
      filter: false,
      viewColumns: false,
      textLabels: {
        body: {
          noMatch:
            tableType == "OperatorsTable"
              ? "No registered operators."
              : "No registered team members.",
        },
      },
      sortOrder: {
        name: "Name",
        direction: "asc",
      },
      searchPlaceholder:
        tableType == "OperatorsTable" ? "Search operators" : "Search users",
    };
  };

  const getTableColumns = (tableType: TableType) => {
    const columns = [];

    columns.push({
      name: tableType == "OperatorsTable" ? "Operators" : "Users",
      options: {
        filter: true,
        sort: true,
        setCellHeaderProps: () => {
          return {
            style: {
              width: 320,
            },
          };
        },
        customBodyRender: renderName,
      },
    });
    columns.push({
      name: "Last active",
      options: {
        filter: true,
        sort: true,
        customBodyRender: renderLastActiveCell,
        sortCompare: sortLastActiveColumn,
      },
    });
    if (props.renderRemoveButton) {
      columns.push({
        name: "",
        options: {
          filter: false,
          sort: false,
          searchable: false,
          customBodyRender: props.renderRemoveButton,
        },
      });
    }

    return columns;
  };

  const renderName = (name: string) => {
    if (name.length > 40) {
      const truncName = name.substring(0, 39) + "...";
      return (
        <Tooltip title={name}>
          <span>{truncName}</span>
        </Tooltip>
      );
    }
    return <span>{name}</span>;
  };

  const getTableData = (members: TeamMember[]): DataRow[] =>
    members.map(user => [
      user.userName,
      {
        status: user.status,
        lastActive: user.lastActive,
      },
      user,
    ]);

  type DataRow = [
    userName: string,
    status: UserStatusWithDate,
    user: TeamMember,
  ];

  type UserStatusWithDate = {
    status: UserStatus;
    lastActive: Date;
  };

  const renderLastActiveCell = (userStatusWithDate: UserStatusWithDate) => {
    return (
      <div style={{ whiteSpace: "nowrap" }}>
        {getLastActivityCell(
          userStatusWithDate.status,
          userStatusWithDate.lastActive,
        )}
      </div>
    );
  };

  const sortLastActiveColumn = (order: string) => {
    return (
      { data: userStatus1 }: { data: UserStatusWithDate },
      { data: userStatus2 }: { data: UserStatusWithDate },
    ) => {
      if (userStatus1.status !== userStatus2.status) {
        return (
          (StatusOrder[userStatus1.status] - StatusOrder[userStatus2.status]) *
          (order === "asc" ? 1 : -1)
        );
      }
      const lastActiveDate1 = new Date(userStatus1.lastActive);
      const lastActiveDate2 = new Date(userStatus2.lastActive);

      return (
        (lastActiveDate1.getTime() - lastActiveDate2.getTime()) *
        (order === "asc" ? 1 : -1)
      );
    };
  };

  const getLastActivityCell = (status: UserStatus, lastActive: Date) => {
    switch (status) {
      case UserStatus.INVITED:
        return (
          <div
            style={{
              whiteSpace: "nowrap",
              verticalAlign: "middle",
              display: "flex",
              gap: "4px",
            }}
          >
            <WarningIcon
              sx={{ color: Warning.default.main }}
              fontSize={"small"}
            />
            <Typography variant="body2" strong color={Warning.container.text}>
              Pending invitation
            </Typography>
          </div>
        );
      case UserStatus.WAITING_FOR_REGISTRATION:
        return (
          <div
            style={{
              whiteSpace: "nowrap",
              verticalAlign: "middle",
              display: "flex",
            }}
          >
            <WarningIcon sx={{ color: Warning.default }} fontSize={"small"} />
            <Typography variant="body2" strong color={Warning.container.text}>
              Waiting for registration
            </Typography>
          </div>
        );
      default:
        return (
          <div
            style={{
              whiteSpace: "nowrap",
              verticalAlign: "middle",
              display: "flex",
            }}
          >
            <Typography variant="body2">
              {lastActive != null
                ? moment(lastActive, "YYYYMMDD hh:mm:ss").utc(true).fromNow()
                : ""}
            </Typography>
          </div>
        );
    }
  };

  const regularTeamMemberList = props.memberList.filter(
    member => !member.operator,
  );
  const operatorMemberList = props.memberList.filter(member => member.operator);

  return (
    <div className="memberList">
      <MUIDataTable
        title={""}
        columns={getTableColumns("UsersTable")}
        data={getTableData(regularTeamMemberList)}
        options={getMuiTableOptions("UsersTable")}
      />
      <div className={"operatorsTable"}>
        <MUIDataTable
          title={""}
          columns={getTableColumns("OperatorsTable")}
          data={getTableData(operatorMemberList)}
          options={getMuiTableOptions("OperatorsTable")}
        />
      </div>
    </div>
  );
}

type Props = {
  memberList: TeamMember[];
  renderRemoveButton?: (teamMember: TeamMember) => React.ReactNode;
};

export default MemberList;
